import React, { useMemo } from 'react';
import { Alert, AlertDescription, AlertIcon, AlertTitle, Box } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import { CenteredLoader } from 'clipsal-cortex-ui/src/components/CenteredLoader';

import { SlidingAnimationPageBase } from '../../../common/components/SlidingAnimationPageBase';
import { IS_DEMO_LOGIN } from '../../../common/constants';
import { useParamBackUrl } from '../../../common/hooks/use-param-back-url';
import { MANUFACTURER_ID_SATURN } from '../../devices/devices-helper';
import { selectCellularModems, selectInverters, selectSite } from '../../site/siteSlice';
import { ConnectionItem } from './components/ConnectionItem';
import { ConnectionsList } from './components/ConnectionsList';
import { DeviceIcon } from './components/DeviceIcon';
import { HARDWARE_CONNECTIONS, HARDWARE_TYPE_TO_UI_CONFIG } from './constants';
import { DetailsConfigurationButton } from './details/DetailsConfigurationButton';
import { DetailsInfo } from './details/DetailsInfo';
import { useGetCellularModemDetailsQuery } from './hardwareApi';
import { VisibleHardwareType } from './types';
import { useDeviceSubtitle } from './use-device-subtitle';

export function HardwareDetails() {
  const { t } = useTranslation();
  const { site_id: siteId } = useSelector(selectSite);
  const { devices } = useSelector(selectSite);
  const modems = useSelector(selectCellularModems);
  const { deviceId } = useParams<{ deviceId: string }>();
  const { getSubtitle } = useDeviceSubtitle();
  const isSaturnInverter = useSelector(selectInverters).find(
    ({ manufacturer_id: mId, id }) => [MANUFACTURER_ID_SATURN].includes(mId) && id === Number(deviceId)
  );
  const isCellularModem = modems.find(({ id }) => id === Number(deviceId));

  const device = useMemo(() => devices.find((device) => device.id === Number(deviceId)), [devices, deviceId]);

  const shouldLoadCellular = (modems.length && isSaturnInverter) || isCellularModem;
  const { data: dataCellular, isLoading: isLoadingCellular } = useGetCellularModemDetailsQuery(siteId, {
    skip: !shouldLoadCellular,
  });
  const isLoading = shouldLoadCellular ? isLoadingCellular : false;

  const { connections, connectionCellular } = useMemo(() => {
    const liveConnections = HARDWARE_CONNECTIONS.map((connection) =>
      connection.type === 'CELLULAR_MODEM' && dataCellular ? dataCellular : connection
    );
    const defaultCellular = HARDWARE_CONNECTIONS.find(({ type }) => type === 'CELLULAR_MODEM');
    return {
      connectionCellular: dataCellular ?? defaultCellular,
      connections: liveConnections.filter(({ enabled }) => !!enabled),
    };
  }, [dataCellular]);

  const { icon, title, subtitle } = useMemo(() => {
    const iconAndTitle = HARDWARE_TYPE_TO_UI_CONFIG[(device?.device_type as VisibleHardwareType) ?? 'INVERTER'];
    const connectionType = connections.find((connection) => connection?.enabled)?.type;

    if (device?.device_type === 'INVERTER') {
      return {
        ...iconAndTitle,
        subtitle: getSubtitle('INVERTER', true, connectionType),
      };
    }

    if (device?.device_type === 'CELLULAR_MODEM') {
      return {
        ...iconAndTitle,
        subtitle: getSubtitle('INVERTER', connectionType === 'CELLULAR_MODEM', 'CELLULAR_MODEM'),
      };
    }
    return {
      ...iconAndTitle,
      subtitle: undefined,
    };
  }, [device?.device_type, connections, getSubtitle]);

  const displayConnectionGroup = useMemo(
    () => !!connections.length && device?.device_type === 'INVERTER',
    [connections, device]
  );

  const displayConnectionItem = useMemo(() => device?.device_type === 'CELLULAR_MODEM', [device]);

  const displayConfiguration = useMemo(
    () =>
      device?.device_type !== 'BACKUP_GRID_CONTROL_SYSTEM' &&
      device?.device_type !== 'CELLULAR_MODEM' &&
      !IS_DEMO_LOGIN,
    [device]
  );

  const backURL = useParamBackUrl(`../../list?direction=back`);

  return (
    <SlidingAnimationPageBase title={t('Hardware.hardware')} backURL={backURL} pb={6}>
      <CenteredLoader hidden={!isLoading} text={`${t('Common.loading')}...`} />
      {device && !isLoading && (
        <Box px={5}>
          <DeviceIcon Icon={icon} title={title} subtitle={subtitle} />

          {displayConnectionGroup && <ConnectionsList connections={connections} />}
          {displayConnectionItem && <ConnectionItem connection={connectionCellular} />}

          <DetailsInfo device={device} connection={dataCellular} />

          {displayConfiguration && <DetailsConfigurationButton device={device} />}
        </Box>
      )}
      {!device && (
        <Alert status="error">
          <AlertIcon />
          <AlertTitle>{t('Hardware.error fetching device')}</AlertTitle>
          <AlertDescription>
            {t('Common.please try again')} {t('Common.if this persists contact support')}
          </AlertDescription>
        </Alert>
      )}
    </SlidingAnimationPageBase>
  );
}
